import { X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef } from 'react';

import { duration, easing } from '../motion/tokens';

import type { VideoAsset } from './media';

/**
 * Accessible video dialog for the hero's "Watch Vaanexa in Action" CTA.
 *
 * Opening is always user-initiated, so playback starting here is not autoplay.
 * It still starts muted — sound is one click away on the native controls, which
 * is the behaviour visitors expect and the one browsers will not block.
 */
export function VideoModal({
  open,
  onClose,
  video,
  title,
}: {
  open: boolean;
  onClose: () => void;
  video: VideoAsset;
  title: string;
}): JSX.Element {
  const closeRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    returnFocusRef.current = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      returnFocusRef.current?.focus();
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-[70] grid place-items-center bg-black/80 p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: duration.standard, ease: easing.entrance }}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          onClick={onClose}
        >
          <motion.div
            className="mk-panel relative w-full max-w-4xl overflow-hidden rounded-2xl"
            initial={{ opacity: 0, y: 18, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.99 }}
            transition={{ duration: duration.section, ease: easing.entrance }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-4 border-b border-[var(--mk-line)] px-4 py-3">
              <p className="text-sm font-semibold text-[var(--mk-ink)]">{title}</p>
              <button
                ref={closeRef}
                type="button"
                onClick={onClose}
                className="grid h-9 w-9 place-items-center rounded-lg border border-[var(--mk-line)] text-[var(--mk-ink-soft)] transition-colors hover:border-[var(--mk-cyan)] hover:text-[var(--mk-ink)]"
                aria-label="Close video"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>

            {/* Reserved by aspect ratio, so opening the dialog never reflows. */}
            <div className="bg-black" style={{ aspectRatio: video.aspect ?? '16 / 9' }}>
              <video
                className="h-full w-full"
                src={video.src}
                poster={video.poster}
                controls
                autoPlay
                muted
                playsInline
                preload="metadata"
                aria-label={video.alt}
              />
            </div>

            {video.isPlaceholder ? (
              <p className="border-t border-[var(--mk-line)] px-4 py-2.5 text-[11px] text-[var(--mk-ink-muted)]">
                Placeholder clip — replace the URL in
                <code className="mx-1 rounded bg-[var(--mk-surface)] px-1.5 py-0.5">
                  src/marketing/media.ts
                </code>
                once the Vaanexa recording is ready.
              </p>
            ) : null}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
